import useScreenSize from '@/hooks/use-screen-size';
import { useLeftSidebar } from '@/hooks/useLeftSidebar';
import { cn } from '@/utils/utils';
import type { ReactNode } from 'react';
import { useIsMounted } from 'usehooks-ts';
import LeftSidebar from '../Sidebars/LeftSidebar';
import SidebarChatsOverlay from '../Sidebars/SidebarChatsOverlay';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  useIsResizing,
} from '../ui/Resizable';

type AppPanelsProps = {
  children: ReactNode;
};

export const AppPanels = ({ children }: AppPanelsProps) => {
  const { showLeftSidebar } = useLeftSidebar();
  const { isResizing } = useIsResizing();
  const isMounted = useIsMounted();

  const screen = useScreenSize();

  const { width } = screen;

  const minSizePixels = 240;
  const minSizePercentage = (minSizePixels / width) * 100;
  const maxSizePixels = 340;
  const maxSizePercentage = (maxSizePixels / width) * 100;

  return (
    <ResizablePanelGroup
      autoSaveId="left-sidebar-main"
      className="h-screen"
      direction="horizontal"
    >
      <ResizablePanel
        maxSize={maxSizePercentage}
        {...(isMounted() && { defaultSize: minSizePercentage })}
        className={cn(!isResizing && 'transition-all duration-200 ease-in-out')}
        {...(!showLeftSidebar && { maxSize: 0 })}
        {...(showLeftSidebar && isMounted() && { minSize: minSizePercentage })}
        order={1}
        id="left-sidebar"
      >
        <LeftSidebar />
      </ResizablePanel>
      <ResizableHandle
        id="left-sidebar"
        className={cn(!showLeftSidebar && 'hidden', 'z-50')}
      />
      <ResizablePanel
        defaultSize={100 - minSizePercentage}
        minSize={100 - maxSizePercentage}
        className={cn(
          !isResizing && 'transition-all duration-200 ease-in-out',
          'relative',
        )}
        order={2}
        id="main-content"
      >
        {children}
        <SidebarChatsOverlay />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
};
